import { useEffect, useState } from "react";

/**
 * Custom hook for responsive design using media queries
 * @param {string} query - CSS media query string (e.g., '(max-width: 640px)')
 * @returns {boolean} - Whether the media query matches
 *
 * @example
 * const isMobile = useMediaQuery('(max-width: 640px)');
 * const isTablet = useMediaQuery('(min-width: 641px) and (max-width: 1024px)');
 * const isLandscape = useMediaQuery('(orientation: landscape)');
 */
export const useMediaQuery = query => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    // Only run on client side (SSR safety)
    if (typeof window === "undefined") return;

    const media = window.matchMedia(query);

    // Create event listener
    const listener = event => setMatches(event.matches);

    // Add listener (modern browsers)
    if (media.addEventListener) {
      media.addEventListener("change", listener);
    } else {
      // Fallback for older browsers
      media.addListener(listener);
    }

    // Cleanup
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", listener);
      } else {
        // Fallback for older browsers
        media.removeListener(listener);
      }
    };
  }, [query]);

  return matches;
};

// Predefined breakpoints using above
export const useIsMobile = () => useMediaQuery("(max-width: 640px)");
export const useIsTablet = () => useMediaQuery("(min-width: 641px) and (max-width: 1024px)");
export const useIsDesktop = () => useMediaQuery("(min-width: 1025px)");
export const useIsLandscape = () => useMediaQuery("(orientation: landscape)");
export const useIsPortrait = () => useMediaQuery("(orientation: portrait)");
